import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AchievementCard from '@/components/AchievementCard';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import { useAchievementOverview } from '@/hooks/useAchievement';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePagination from '@/hooks/usePagination';
import PersonContext from '@/pages/Person/PersonContext';
import { AchievementOverviewParams } from '@/types/achievement';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

const Achievement = () => {
  const { navigateToCreatePersonAchievement } = useNavigateToPage();
  const { person } = useContext(PersonContext);
  const [searchParams, setSearchParams] = useSearchParams({ page: '1' });
  const [totalPages, setTotalPages] = useState<number>(0);
  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +(searchParams.get('page') ?? '1'),
    totalPages
  );

  const [params, setParams] = useState<AchievementOverviewParams>({ page: '1' });

  const {
    data: overviewResponse,
    isPending,
    isSuccess
  } = useAchievementOverview(params, person.id);

  useEffect(() => {
    if (isSuccess) {
      if (overviewResponse?.page) {
        setSearchParams((currentParams) => {
          currentParams.set('page', overviewResponse.page.toString());
          return currentParams;
        });
      }

      if (overviewResponse?.total_pages) {
        setTotalPages(overviewResponse.total_pages);
      }
    }
  }, [isSuccess, overviewResponse, setSearchParams]);

  useEffect(() => {
    setSearchParams((currentParams) => {
      currentParams.set('page', currentPage.toString());
      return currentParams;
    });
  }, [currentPage, setSearchParams]);

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage]);

  return (
    <Container type="page">
      <PageHeader headingText="Achievements" className="sr-only" />
      <div
        className={`flex justify-between items-center mb-4 pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <Button
          className="bg-blue-500 text-white px-4 py-2 rounded-sm hover:bg-blue-600"
          onClick={() =>
            navigateToCreatePersonAchievement(person.id, overviewResponse?.results ?? [])
          }>
          Award New Achievement
        </Button>
      </div>

      <div className="border-t-2 border-gray-200 py-2">
        <Pagination
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}>
          {isPending ? (
            <div className="my-2">
              <LoadingSkeleton spinnerProps={{ label: 'Loading Achievements' }} />
            </div>
          ) : !overviewResponse?.results.length ? (
            <div className="flex-1 flex items-center justify-center">
              <span className="text-red-500">There are no achievements available.</span>
            </div>
          ) : (
            overviewResponse?.results?.map((result, index) => (
              <React.Fragment key={`${index}-container`}>
                <Heading className="text-2xl font-bold mt-4 mb-2">{result.name}</Heading>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {result.achievements.map((achievement, idx) => (
                    <AchievementCard key={`${idx}-achievement`} achievement={achievement} />
                  ))}
                </div>
              </React.Fragment>
            ))
          )}
        </Pagination>
      </div>
    </Container>
  );
};

export default Achievement;
